import {Component} from '@angular/core';
import {MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {TranslateService} from '@ngx-translate/core';
import {IdexService} from '../../idex.service';
import {Toast} from '../../shared/toast/toast';

@Component({
   selector: 'app-allow-ip-dialog',
   templateUrl: './allow-ip-dialog.component.html',
   styleUrls: ['./allow-ip-dialog.component.css']
})
export class AllowIpDialogComponent {
   siteCode: string;
   item: any;
   ipAddress = '';
   description = '';
   isPrefix = false;
   error = '';
   formatError = '';
   submitting = false;

   constructor(public service: IdexService, public toast: Toast,
               private dialogRef: MatDialogRef<AllowIpDialogComponent>,
               private translate: TranslateService) {
   }

   // called by opener before showing
   init(siteCode: string, item?: any) {
      this.siteCode = siteCode;
      if (item) {
         this.item = item;
         this.ipAddress = item.ipAddress;
         this.description = item.description || '';
         this.isPrefix = !!item.isPrefix;
      }
   }

   // Re-run format validation whenever the input or the prefix toggle changes.
   validate(): void {
      const key = this.checkFormat(this.ipAddress.trim());
      this.formatError = key ? this.translate.instant(key) : '';
   }

   get isValid(): boolean {
      return this.ipAddress.trim() !== '' && this.formatError === '';
   }

   /** Returns a translate key for the format error, or '' when the value is a valid IP (or prefix). */
   private checkFormat(ip: string): string {
      if (!ip) { return ''; }
      const isIpv6 = ip.indexOf(':') >= 0;
      if (this.isPrefix) {
         if (isIpv6) {
            return this.isIpv6Prefix(ip) ? '' : 'company.allow-invalid-ipv6-prefix';
         }
         return this.isIpv4Prefix(ip) ? '' : 'company.allow-invalid-ipv4-prefix';
      }
      if (isIpv6) {
         return this.isIpv6(ip) ? '' : 'company.allow-invalid-ipv6';
      }
      return this.isIpv4(ip) ? '' : 'company.allow-invalid-ipv4';
   }

   private isIpv4(s: string): boolean {
      const parts = s.split('.');
      return parts.length === 4 && parts.every(p => /^\d{1,3}$/.test(p) && +p <= 255);
   }

   // A leading portion of an IPv4 address, e.g. "203.", "203.0.113." or "203.0.113.10".
   private isIpv4Prefix(s: string): boolean {
      if (!/^[0-9.]+$/.test(s) || !/\d/.test(s)) { return false; }
      const parts = s.split('.');
      if (parts.length > 4) { return false; }
      for (let i = 0; i < parts.length; i++) {
         const p = parts[i];
         if (p === '') {
            if (i !== parts.length - 1) { return false; } // empty only allowed as trailing dot
            continue;
         }
         if (!/^\d{1,3}$/.test(p) || +p > 255) { return false; }
      }
      return true;
   }

   private isIpv6(s: string): boolean {
      if (s.indexOf(':') < 0) { return false; }
      if ((s.match(/::/g) || []).length > 1) { return false; }
      const groups = s.split(':');
      if (groups.some(g => g !== '' && !/^[0-9a-fA-F]{1,4}$/.test(g))) { return false; }
      const nonEmpty = groups.filter(g => g !== '').length;
      if (s.indexOf('::') >= 0) { return nonEmpty <= 7; }
      return groups.length === 8;
   }

   // A leading portion of an IPv6 address, e.g. "2001:db8:" or "2001:44c8".
   private isIpv6Prefix(s: string): boolean {
      if (!/^[0-9a-fA-F:]+$/.test(s) || !/[0-9a-fA-F]/.test(s)) { return false; }
      if ((s.match(/::/g) || []).length > 1) { return false; }
      const groups = s.split(':');
      if (groups.length > 8) { return false; }
      return groups.every(g => g === '' || /^[0-9a-fA-F]{1,4}$/.test(g));
   }

   save() {
      const ip = this.ipAddress.trim();
      const key = this.checkFormat(ip);
      this.formatError = key ? this.translate.instant(key) : '';
      if (!ip || this.formatError) { return; }
      this.submitting = true;
      this.error = '';
      const body = {ipAddress: ip, description: this.description, isPrefix: this.isPrefix};
      const req = this.item
         ? this.service.updateAllowedIp(this.siteCode, this.item.id, body)
         : this.service.createAllowedIp(this.siteCode, body);
      req.subscribe(() => {
         this.dialogRef.close(true);
      }, (e: any) => {
         this.submitting = false;
         this.error = this.mapSaveError(e);
      });
   }

   // Translate known backend errors (the duplicate case) and fall back to the raw message.
   private mapSaveError(e: any): string {
      const msg = (e && e.message) ? e.message : '';
      if (msg.indexOf('already exists') >= 0) {
         return this.translate.instant('company.allow-duplicate-ip');
      }
      return msg || 'Save failed';
   }
}
